// ============================================================================
// OBT F2-B — Formen görev listesi (my-publications) hook'u
// ============================================================================
// Mevcut {data, loading, error, reload} deseni (react-query yok). Sayaçlar ve
// süresi-geçti bilgisi GERÇEK backend yanıtından gelir; sahte sayaç üretilmez.
// Kategori filtresi YALNIZ backend sayaçlarından türetilir (istemci tarafı).
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listMyPublications } from './responseApi';
import { normalizeResponseError } from './responseErrors';
import { publicationMatchesFilter } from './responseEnums';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

// Görev listesi arka planda tazelenir (bildirim ziliyle aynı ritim): OBT
// sekmesinde beklerken yeni yayınlanan talep manuel "Yenile" GEREKTİRMEDEN düşer.
const POLL_MS = 45_000;

export function useMyPublications({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    items: [],
  });
  const [filter, setFilter] = useState('all');
  const abortRef = useRef(null);

  // `silent`: arka plan yoklaması — spinner GÖSTERME (liste titremesin), yalnız
  // veriyi tazele. İlk yükleme ve manuel "Yenile" silent=false (spinner çıkar).
  const load = useCallback(
    async (silent = false) => {
      if (!enabled) {
        setState({ loading: false, error: null, items: [] });
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!silent) setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const items = await listMyPublications({ signal: controller.signal });
        if (controller.signal.aborted) return;
        setState({ loading: false, error: null, items });
      } catch (error) {
        if (isCanceled(error) || controller.signal.aborted) return;
        // Arka plan yoklaması başarısızsa mevcut listeyi KORU (ekranı bozma).
        if (!silent) {
          setState({ loading: false, error: normalizeResponseError(error), items: [] });
        }
      }
    },
    [enabled]
  );

  useEffect(() => {
    load();
    if (!enabled) return () => abortRef.current?.abort();
    const timer = setInterval(() => load(true), POLL_MS);
    // Sekmeye/geri dönüldüğünde anında tazele (kaydırmadan).
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      abortRef.current?.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, enabled]);

  const items = Array.isArray(state.items) ? state.items : [];
  const filtered = useMemo(
    () => items.filter((p) => publicationMatchesFilter(p, filter)),
    [items, filter]
  );

  // Manuel "Yenile" spinner'lı yükleme yapsın (tıklama olayı `silent`'e sızmasın).
  const reload = useCallback(() => load(false), [load]);

  return {
    ...state,
    items,
    filtered,
    filter,
    setFilter,
    reload,
  };
}
